<i18n src="./resources/locales.json"></i18n>
<template>
  <v-combobox
    class="sms-contact-group-selection"
    ref="selectionBox"
    id="sms-contact-number-input"
    v-model="selectedContacts"
    item-text="number"
    item-value="number"
    :auto-select-first="autoFirst"
    :items="eligibleContacts"
    :filter="filterContactsWithSearchInput"
    :label="$t('inputLabel')"
    :menu-props="{ zIndex: 1000 }"
    :placeholder="$t('searchPlaceholder')"
    :search-input.sync="searchInput"
    background-color="var(--v-surface2-base)"
    @change="clearSearchValue"
    @update:search-input="handleFilteredItemsChange"
    @keydown.enter="setSelection"
    deletable-chips
    filled
    hide-selected
    hide-details
    multiple
    full-width
    attach
    :suffix="newMessageGroupMode ? selectedContacts.length + '/' + MAX_GROUP_CHAT_NUMBERS : ''">

    <template v-slot:selection="{ item, parent }">
      <SmsChipItem :contact="selectedContact(item)" @close="parent.selectItem(item)" />
    </template>
    <template v-slot:item="data">
      <SmsContactItem :contact="data.item" />
    </template>
    <template v-slot:no-data>
      <v-list-item class="contact-list">
        <v-list-item-content v-html="noDataMessageText"></v-list-item-content>
      </v-list-item>
    </template>
  </v-combobox>
</template>

<script>
import { mapGetters } from 'vuex';
import Contact from '@/classes/v2/Contact';
import SmsChipItem from '@/components/SmsGroups/SmsChipItem';
import SmsContactItem from '@/components/SmsContactItem/SmsContactItem';
import ToastMixin from '@/mixins/ToastMixin';
import ValidationMixin from '@/mixins/ValidationMixin';

export default {
  name: 'SmsContactNumberInput',

  components: { SmsChipItem, SmsContactItem },

  mixins: [ToastMixin, ValidationMixin],

  data() {
    return {
      autoFirst: true,
      searchInput: '',
      selectedContacts: [],
      MAX_GROUP_CHAT_NUMBERS: 9
    };
  },

  props: {
    newMessageGroupMode: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    ...mapGetters('contactsV2', ['currentUserContacts', 'enterpriseContacts']),
    ...mapGetters('conversationsV2', ['activeConversationId']),

    eligibleContacts() {
      let eligibleContacts = [];
      // Add header and user contacts
      if (this.currentUserContacts.length > 0) {
        eligibleContacts.push({ header: this.$t('myContacts') });
        eligibleContacts = eligibleContacts.concat(this.currentUserContacts);
        // Add divider
        if (this.enterpriseContacts.length > 0) {
          eligibleContacts.push({ divider: true });
        }
      }
      // Add header and account contacts
      if (this.enterpriseContacts.length > 0) {
        eligibleContacts.push({ header: this.$t('entContacts') });
        eligibleContacts = eligibleContacts.concat(this.enterpriseContacts);
      }

      return eligibleContacts;
    },

    isSearchDataValidNumber() {
      return this.isValidSelectionOrInput(this.searchInput) === true;
    },

    noDataMessageText() {
      return this.$t(this.isSearchDataValidNumber ? 'noDataText' : 'noDataTextInvalid', { to: this.searchInput });
    }
  },

  watch: {
    selectedContacts() {
      this.$emit('change', this.selectedContacts);
      this.$refs.selectionBox.focus();
    },
    activeConversationId() {
      this.$emit('change', this.selectedContacts);
      this.$refs.selectionBox.focus();
    },
    newMessageGroupMode() {
      this.cleanUpContactsForGroup(true);
    }
  },

  mounted() {
    this.$root.$on('add-contact-to-contact-selection', this.addContactToContactSelection);
    this.$root.$on('clear-contact-selection', this.clearContactSelection);
  },

  beforeDestroy() {
    this.$root.$off('add-contact-to-contact-selection');
    this.$root.$off('clear-contact-selection');
  },

  methods: {
    _cleanUpInvalidNumbers() {
      this.$nextTick(() => {
        this.selectedContacts = this.selectedContacts.filter(contact => {
          return this.isValidSelectionOrInput(contact) === true;
        });
      });
    },

    addContactToContactSelection(contact) {
      if (!contact) return;

      this.selectedContacts.push(contact);
      this.cleanUpContactsForGroup();
    },

    clearContactSelection() {
      this.selectedContacts = [];
    },

    /**
     * v-combobox doesnt natively support onFilteredItemsChange
     * and its prop auto-select-first breaks press Enter functionality
     * for non-matches
     */
    handleFilteredItemsChange() {
      const filteredItems = this.$refs.selectionBox.filteredItems;
      this.autoFirst = filteredItems.length >= 1;
    },

    isValidSelectionOrInput(val) {
      let isValid = true;
      if (typeof val === 'object') {
        isValid = this.isValidPhoneNumber(val?.number);
      } else {
        isValid = this.isValidPhoneNumber(val);
      }
      return isValid;
    },

    filterContactsWithSearchInput(contact, queryText) {
      if (contact.header || contact.divider) return false;
      return contact.includes(queryText);
    },

    clearSearchValue() {
      this._cleanUpInvalidNumbers();
      this.searchInput = '';
      this.cleanUpContactsForGroup();
    },

    cleanUpContactsForGroup(removeNumbers){
      let showedError = false;
      while(this.newMessageGroupMode && this.selectedContacts.length > this.MAX_GROUP_CHAT_NUMBERS){
        if(!showedError){
          showedError = true;
          this.showError(this.$t('errorMessageMaxGroupChatLimit') + (removeNumbers ? this.$t("errorMessageMaxGroupChatLimitRemove") : this.$t("errorMessageMaxGroupChatLimitPrevent") ))
        }
        this.selectedContacts.splice(this.selectedContacts.length - 1, 1);
      }
    },

    selectedContact(item) {
      if (item instanceof Contact) {
        return item;
      } else {
        return new Contact({ number: item })
      }
    },

    /**
     * Adds the current user input value to selectedContacts when no matching
     * value is found
     */
    setSelection() {
      if (this.autoFirst === false) {
        if (this.isSearchDataValidNumber) {
          this.addContactToContactSelection(this.searchInput);
        }
        // Prevents a double input due to dynamic autoFirst (CANNOT USE this.clearSearchValue())
        this.$refs.selectionBox.internalSearch = null;
      }
    }
  }
};
</script>

<style scoped>
.sms-contact-group-selection >>> .v-input__slot {
  min-height: 59px;
}
.sms-contact-group-selection >>> .v-input__slot:before {
  border-color: var(--v-surface-border-darken1) !important;
}
.sms-contact-group-selection >>> .v-input__append-inner{
  margin: auto;
}
</style>
